'use client';
import { realtimeDatabase } from '@util/firebase';
import type { OnlineStatus } from '@util/hooks/useOnlineStatus';
import { logError } from '@util/logError';
import {
  get,
  onDisconnect,
  onValue,
  ref,
  serverTimestamp,
  update,
} from 'firebase/database';
import Cookies from 'js-cookie';
import { useCallback, useEffect, useRef } from 'react';

const buildStatus = (state: 'online' | 'offline', ip?: string) => {
  const ts = serverTimestamp();
  return {
    state,
    last_changed: ts,
    web: ts,
    web_info: { ua: navigator.userAgent },
    ...(ip
      ? {
          [`access_logs/${ip}/${
            state === 'online' ? 'connected' : 'disconnected'
          }`]: ts,
        }
      : {}),
  };
};

/**
 * Manages the signed-in user's own realtime presence (/status/{uid}).
 *
 * Marks the user online whenever the uid changes or the realtime database
 * connection is (re)established, and registers an onDisconnect handler so the
 * backend flips them offline when the connection drops. The IP address is
 * best-effort: presence writes never wait on it, it only enriches access_logs.
 *
 * `onFirstConnect` fires once per uid with the previous status (read before
 * the first online write), e.g. to decide whether to show a welcome-back UI.
 *
 * For displaying another user's status, use `useOnlineStatus` instead.
 */
export const useMyPresence = (
  uid?: string,
  onFirstConnect?: (previousStatus: OnlineStatus | null, uid: string) => void
) => {
  const currentStateRef = useRef<'online' | 'offline' | null>(null);
  const ipRef = useRef<string | undefined>(undefined);

  // keep the callback fresh without re-subscribing presence listeners
  const onFirstConnectRef = useRef(onFirstConnect);
  useEffect(() => {
    onFirstConnectRef.current = onFirstConnect;
  }, [onFirstConnect]);

  const setOffline = useCallback(async () => {
    if (!uid) return;
    try {
      currentStateRef.current = 'offline';
      await update(
        ref(realtimeDatabase, `/status/${uid}`),
        buildStatus('offline', ipRef.current)
      );
    } catch (e) {
      logError(e, 'useMyPresence.setOffline');
    }
  }, [uid]);

  useEffect(() => {
    if (!uid) return;
    // don't overwrite the impersonated user's presence
    if (Cookies.get('impersonated')) return;

    currentStateRef.current = null;
    let firstConnect = true;

    const statusRef = ref(realtimeDatabase, `/status/${uid}`);
    const connectedRef = ref(realtimeDatabase, '.info/connected');

    // IP is optional: presence writes never wait on it
    fetch('/api/ip')
      .then((res) => {
        ipRef.current =
          res.headers.get('x-client-ip')?.replaceAll('.', '_') || undefined;
      })
      .catch(() => {});

    function setState(next: 'online' | 'offline') {
      if (currentStateRef.current === next) return;
      currentStateRef.current = next;

      update(statusRef, buildStatus(next, ipRef.current)).catch((e) =>
        logError(e, 'useMyPresence.setState')
      );
    }

    const connectedUnsub = onValue(connectedRef, (snap) => {
      if (!snap.val()) {
        // connection dropped: the registered onDisconnect flips us offline
        // server-side, so reset local state to re-write online on reconnect
        currentStateRef.current = null;
        return;
      }

      // register the disconnect handler before going online so a dropped
      // connection can't leave the user stuck as online
      onDisconnect(statusRef)
        .update(buildStatus('offline', ipRef.current))
        .catch((e) => logError(e, 'useMyPresence.onDisconnectRegister'));

      const goOnline = async () => {
        if (firstConnect) {
          firstConnect = false;
          // read the previous status before we overwrite last_changed
          try {
            const previous = (await get(statusRef)).val();
            onFirstConnectRef.current?.(previous, uid);
          } catch (e) {
            logError(e, 'useMyPresence.onFirstConnect');
          }
        }
        setState('online');
      };

      void goOnline();
    });

    return () => {
      connectedUnsub();
    };
  }, [uid]);

  return { setOffline };
};
